import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Bank, DownloadSimple, ArrowLeft, Plus, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import DirectDepositForm from "../components/DirectDepositForm";
import { useT } from "../lib/i18n";

export default function DirectDeposits() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [paying, setPaying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/direct-deposits");
      setDeposits(r.data.deposits || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dd.error.load"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Handle the Stripe-redirect ?paid=<deposit_id>&session_id=… and ?cancelled=…
  useEffect(() => {
    const paidId = params.get("paid");
    const cancelledId = params.get("cancelled");
    if (paidId) {
      toast.success("Payment received — Zcardon will wire the funds within 1 business day.");
      const next = new URLSearchParams(params); next.delete("paid"); next.delete("session_id"); setParams(next, { replace: true });
      load();
    } else if (cancelledId) {
      toast.message("Payment cancelled — your deposit is still saved, you can pay any time.");
      const next = new URLSearchParams(params); next.delete("cancelled"); setParams(next, { replace: true });
    }
  }, [params, setParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = async (id, ref) => {
    try {
      const r = await api.get(`/direct-deposits/${id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `zcardon-deposit-${ref}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dd.error.pdf"));
    }
  };

  const startCheckout = async (id) => {
    setPaying(id);
    try {
      const r = await api.post(`/direct-deposits/${id}/checkout`);
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Could not start payment");
        setPaying(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
      setPaying(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard" className="text-[#4A5C50] hover:text-[#184A30]" data-testid="dd-back">
            <ArrowLeft size={18} />
          </Link>
          <h1 className="serif text-2xl text-[#112318]">{t("dd.title")}</h1>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5">
          <p className="text-sm text-[#4A5C50] leading-relaxed">
            {t("dd.intro")}
          </p>
        </div>

        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="w-full rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white h-12"
            data-testid="dd-page-new"
          >
            <Plus size={16} className="mr-2" /> {t("dd.new_btn")}
          </Button>
        )}

        {showForm && (
          <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="dd-page-form-wrap">
            <DirectDepositForm
              onSuccess={() => { load(); /* keep the form open so the success screen + notify button stay visible */ }}
            />
            <Button
              variant="ghost"
              onClick={() => setShowForm(false)}
              className="mt-2 text-[#4A5C50]"
              data-testid="dd-page-form-cancel"
            >
              {t("dd.close")}
            </Button>
          </div>
        )}

        <section data-testid="dd-page-history">
          <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] mb-3">{t("dd.history_eyebrow")}</p>
          {loading && <p className="text-sm text-[#4A5C50]">{t("dd.loading")}</p>}
          {!loading && deposits.length === 0 && (
            <p className="text-sm text-[#4A5C50]">{t("dd.empty")}</p>
          )}
          {!loading && deposits.length > 0 && (
            <ul className="divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-2xl bg-white">
              {deposits.map((d) => (
                <li key={d.id} className="flex items-center gap-3 p-4" data-testid={`dd-row-${d.reference_number}`}>
                  <Bank size={20} weight="duotone" className="text-[#184A30] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#112318] flex items-center gap-2">
                      <span className="serif font-semibold">${Number(d.amount).toFixed(2)} {d.currency}</span>
                      <span className={`text-[10px] mono px-1.5 py-0.5 rounded ${
                        d.status === "paid_to_zcardon" || d.status === "wired_to_receiver" || d.status === "completed"
                          ? "bg-emerald-100 text-emerald-800"
                          : d.status === "awaiting_payment"
                          ? "bg-blue-100 text-blue-800"
                          : d.status === "approved"
                          ? "bg-[#184A30]/10 text-[#184A30]"
                          : d.status === "pending_approval"
                          ? "bg-[#CBA35C]/15 text-[#8C6B21]"
                          : "bg-[#D96C4A]/15 text-[#B04E2F]"
                      }`}>
                        {d.status === "paid_to_zcardon"
                          ? "Paid · wire pending"
                          : d.status === "wired_to_receiver" || d.status === "completed"
                          ? "Wired to receiver"
                          : d.status === "awaiting_payment"
                          ? "Awaiting your payment"
                          : d.status === "approved"
                          ? t("dd.status.approved")
                          : d.status === "pending_approval"
                          ? t("dd.status.pending")
                          : t("dd.status.rejected")}
                      </span>
                    </p>
                    <p className="text-[11px] mono text-[#4A5C50] mt-0.5 truncate">
                      {d.receiver_name} · {d.bank_name} · {d.account_number_masked}
                    </p>
                    <p className="text-[10px] mono text-[#4A5C50]">
                      {d.reference_number} · {new Date(d.created_at).toLocaleString()}
                    </p>
                    {d.receiver_confirmed_at && (
                      <p className="text-[10px] text-[#184A30] mt-0.5" data-testid={`dd-row-confirmed-${d.reference_number}`}>
                        {t("dd.row.confirmed")}
                      </p>
                    )}
                    {d.receiver_disputed_at && (
                      <p className="text-[10px] text-[#B04E2F] mt-0.5" data-testid={`dd-row-disputed-${d.reference_number}`}>
                        {t("dd.row.disputed")}
                      </p>
                    )}
                  </div>
                  {(d.status === "approved" || d.status === "awaiting_payment") && (
                    <Button
                      size="sm"
                      onClick={() => startCheckout(d.id)}
                      disabled={paying === d.id}
                      className="rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-white"
                      data-testid={`dd-row-pay-${d.reference_number}`}
                    >
                      <CreditCard size={14} className="mr-1" />
                      {paying === d.id ? "Loading…" : `Pay $${Number(d.amount).toFixed(2)}`}
                    </Button>
                  )}
                  {(d.status === "approved" || d.status === "paid_to_zcardon" || d.status === "wired_to_receiver" || d.status === "completed" || d.status === "awaiting_payment") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPdf(d.id, d.reference_number)}
                      className="rounded-lg"
                      data-testid={`dd-row-pdf-${d.reference_number}`}
                    >
                      <DownloadSimple size={14} className="mr-1" /> {t("dd.row.pdf")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
